<template>
  <div class="z-location-picker">
    <div class="z-map-frame">
      <l-map
        ref="map"
        :zoom="zoom"
        :center="center"
        class="z-map"
        @click="onMapClick"
        @ready="onReady"
      >
        <l-tile-layer
          :url="url"
          :attribution="attribution"
        />
        <l-marker
          v-if="hasPosition"
          :lat-lng="position"
          :draggable="true"
          @dragend="onDragEnd"
        />
      </l-map>

      <!-- چیپ مختصات روی نقشه -->
      <transition name="fade">
        <div
          v-if="hasPosition"
          class="z-coord-chip"
        >
          <feather-icon
            icon="MapPinIcon"
            size="14"
          />
          <span>{{ Number(latitude).toFixed(5) }}, {{ Number(longitude).toFixed(5) }}</span>
        </div>
      </transition>

      <!-- دکمه‌ی تمرکز روی نشانگر -->
      <button
        v-if="hasPosition"
        type="button"
        class="z-focus-btn"
        title="تمرکز روی موقعیت"
        @click="focus"
      >
        <feather-icon
          icon="CrosshairIcon"
          size="16"
        />
      </button>
    </div>

    <div class="z-map-hint">
      <feather-icon
        icon="InfoIcon"
        size="14"
        class="mr-25"
      />
      <span>برای انتخاب موقعیت، روی نقشه کلیک کنید یا نشانگر را جابه‌جا کنید.</span>
    </div>
  </div>
</template>

<script>

import { LMap, LTileLayer, LMarker } from 'vue2-leaflet'
import { Icon } from 'leaflet'
// نکته: استایل leaflet.css در public/index.html از CDN لود می‌شود (نه import این‌جا)
// تا postcss-rtl خاصیت‌های left/right آن را در RTL جابه‌جا نکند.

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// رفع مشکل آدرس آیکون پیش‌فرض نشانگر در باندل webpack
// eslint-disable-next-line no-underscore-dangle
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

export default {
  name: 'ZLocationPicker',
  components: { LMap, LTileLayer, LMarker },
  props: {
    latitude: {
      type: [Number, String],
      default: null,
    },
    longitude: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; OpenStreetMap contributors',
      zoom: 5,
      center: [32.4279, 53.6880], // مرکز ایران
    }
  },
  computed: {
    hasPosition() {
      return this.latitude != null && this.latitude !== '' && this.longitude != null && this.longitude !== ''
    },
    position() {
      return [Number(this.latitude), Number(this.longitude)]
    },
  },
  watch: {
    // اولین باری که مختصات تنظیم می‌شود (بارگذاری فرم ویرایش یا اولین کلیک) روی نقطه زوم کن
    hasPosition(now, before) {
      if (now && !before) {
        this.focus()
      }
    },
  },
  mounted() {
    if (this.hasPosition) {
      this.focus()
    }
    // اطمینان از محاسبه‌ی صحیح اندازه‌ی نقشه پس از کامل‌شدن چیدمان کارت/overlay
    setTimeout(this.fixSize, 300)
  },
  methods: {
    onReady() {
      this.$nextTick(this.fixSize)
    },
    fixSize() {
      const map = this.$refs.map && this.$refs.map.mapObject
      if (map) {
        map.invalidateSize()
        if (this.hasPosition) {
          map.setView(this.position, this.zoom)
        }
      }
    },
    onMapClick(e) {
      this.emit(e.latlng.lat, e.latlng.lng)
    },
    onDragEnd(e) {
      const latlng = e.target.getLatLng()
      this.emit(latlng.lat, latlng.lng)
    },
    emit(lat, lng) {
      this.$emit('update:latitude', this.round(lat))
      this.$emit('update:longitude', this.round(lng))
    },
    round(v) {
      return Math.round(v * 1e6) / 1e6
    },
    focus() {
      this.center = [Number(this.latitude), Number(this.longitude)]
      this.zoom = 13
      // علاوه بر prop واکنشی، مستقیماً روی نقشه setView بزن تا پس از
      // محاسبه‌ی اندازه‌ی صحیح کانتینر، باکس واقعاً روی نشانگر متمرکز شود.
      this.$nextTick(() => {
        const map = this.$refs.map && this.$refs.map.mapObject
        if (map) {
          map.invalidateSize()
          map.setView(this.position, this.zoom)
        }
      })
    },
  },
}
</script>

<style>
/* نقشه باید LTR بماند تا در پنل RTL، pane و مرکز زوم درست محاسبه شوند */
/*rtl:ignore*/
.leaflet-container {
  direction: ltr;
}

/* قاب نقشه */
.z-location-picker .z-map-frame {
  position: relative;
  border-radius: 0.6rem;
  overflow: hidden;
  border: 1px solid #e6e6ee;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.08);
}

.z-location-picker .z-map {
  height: 350px;
  width: 100%;
  z-index: 0;
}

/* کنترل‌های زوم لیفلت کمی شیک‌تر */
.z-location-picker .leaflet-control-zoom {
  border: none !important;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.15) !important;
  border-radius: 0.5rem !important;
  overflow: hidden;
}
.z-location-picker .leaflet-control-zoom a {
  border: none !important;
  color: #6e6b7b !important;
}
.z-location-picker .leaflet-control-zoom a:hover {
  background: #f3f2f7 !important;
  color: #7367f0 !important;
}

/* چیپ مختصات */
.z-location-picker .z-coord-chip {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 401;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #fff;
  background: rgba(115, 103, 240, 0.92);
  border-radius: 2rem;
  box-shadow: 0 3px 10px rgba(115, 103, 240, 0.4);
  backdrop-filter: blur(2px);
  direction: ltr;
}

/* دکمه‌ی تمرکز */
.z-location-picker .z-focus-btn {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 401;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background: #fff;
  color: #6e6b7b;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.18);
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}
.z-location-picker .z-focus-btn:hover {
  color: #7367f0;
  transform: translateY(-1px);
}

/* راهنما */
.z-location-picker .z-map-hint {
  display: flex;
  align-items: center;
  margin-top: 0.6rem;
  padding: 0.4rem 0.6rem;
  font-size: 0.78rem;
  color: #6e6b7b;
  background: #f8f8fb;
  border-radius: 0.4rem;
}

/* انیمیشن چیپ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
